import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, ArrowLeft, X, Lock, AlertCircle, BarChart3, ShieldCheck, FileCheck, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as HeroIconsOutline from '@heroicons/react/24/outline';

const LucideIconMap = {
    Lock, AlertCircle, BarChart3, ShieldCheck, FileCheck, CheckCircle2
};

const ComplianceModal = ({ isOpen, onClose, onSubmit, item, isEditing }) => {
    const [formData, setFormData] = useState({
        text: '',
        icon: 'ShieldCheck',
        color: 'blue',
        is_active: true
    });

    useEffect(() => {
        if (item) {
            setFormData(item);
        } else {
            setFormData({
                text: '',
                icon: 'ShieldCheck',
                color: 'blue',
                is_active: true
            });
        }
    }, [item, isOpen]);

    const iconOptions = ['CheckCircle2', 'Lock', 'AlertCircle', 'BarChart3', 'ShieldCheck', 'FileCheck'];
    const colorOptions = ['blue', 'green', 'gold', 'purple', 'red'];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl transform transition-all">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-slate-800">
                        {isEditing ? 'Edit Compliance Item' : 'Add New Item'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Description Text</label>
                        <textarea
                            value={formData.text}
                            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. Adherence to RBI guidelines..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Icon</label>
                        <select
                            value={formData.icon}
                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                        >
                            {iconOptions.map(icon => (
                                <option key={icon} value={icon}>{icon}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Color Theme</label>
                        <select
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                        >
                            {colorOptions.map(color => (
                                <option key={color} value={color}>{color.charAt(0).toUpperCase() + color.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active !== false}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Set as Active</label>
                    </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(formData)}
                        className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
                    >
                        {isEditing ? 'Save Changes' : 'Add Item'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const GovernanceEthicsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sectionData, setSectionData] = useState({
        section_tag: '',
        title: '',
        description: '',
        extra_text: '',
        content_data: []
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/admin/governance/section/ethics');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                const parsedContent = typeof data.content_data === 'string'
                    ? JSON.parse(data.content_data)
                    : (data.content_data || []);

                setSectionData({
                    section_tag: data.section_tag || '',
                    title: data.title || '',
                    description: data.description || '',
                    extra_text: data.extra_text || '',
                    content_data: parsedContent
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching ethics data:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSaving(true);
            await axios.put('/api/admin/governance/section/ethics', {
                ...sectionData,
                content_data: sectionData.content_data
            });
            toast.success('Section settings updated successfully');
        } catch (error) {
            console.error('Error saving ethics data:', error);
            toast.error('Failed to save data');
        } finally {
            setSaving(false);
        }
    };

    const saveContentData = async (newContentData) => {
        try {
            await axios.put('/api/admin/governance/section/ethics', {
                ...sectionData,
                content_data: newContentData
            });
            setSectionData(prev => ({ ...prev, content_data: newContentData }));
            return true;
        } catch (error) {
            console.error('Error updating compliance items:', error);
            toast.error('Failed to update compliance items');
            return false;
        }
    };

    const handleModalSubmit = async (itemData) => {
        let newContentData = [...sectionData.content_data];
        if (editingIndex !== null) {
            newContentData[editingIndex] = itemData;
            toast.success('Item updated');
        } else {
            newContentData.push(itemData);
            toast.success('Item added');
        }

        const success = await saveContentData(newContentData);
        if (success) {
            setIsModalOpen(false);
            setEditingIndex(null);
        }
    };

    const handleDelete = async (index) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            const newContentData = sectionData.content_data.filter((_, i) => i !== index);
            await saveContentData(newContentData);
            toast.success('Item deleted');
        }
    };

    const getColorGradient = (color) => {
        const gradients = {
            blue: 'from-blue-500 to-indigo-600',
            green: 'from-emerald-400 to-green-600',
            gold: 'from-amber-400 to-orange-500',
            purple: 'from-purple-500 to-indigo-600',
            red: 'from-red-500 to-rose-600'
        };
        return gradients[color] || gradients['blue'];
    };

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="space-y-6 pb-12">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Governance - Ethics Section</h1>
                    <p className="text-slate-500 mt-1">Manage "Culture of Ethics" section content</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Tag</label>
                        <input
                            type="text"
                            value={sectionData.section_tag}
                            onChange={(e) => setSectionData({ ...sectionData, section_tag: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Culture of Ethics"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Title</label>
                        <input
                            type="text"
                            value={sectionData.title}
                            onChange={(e) => setSectionData({ ...sectionData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Doing the Right Thing"
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Description/Intro</label>
                    <textarea
                        value={sectionData.description}
                        onChange={(e) => setSectionData({ ...sectionData, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Footer Text</label>
                    <textarea
                        value={sectionData.extra_text}
                        onChange={(e) => setSectionData({ ...sectionData, extra_text: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-md transition-all font-medium"
                    >
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Compliance Items Grid */}
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Compliance Items</h2>
                        <p className="text-sm text-slate-500">Manage the list of compliance points</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingIndex(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all font-semibold"
                    >
                        <Plus size={20} /> Add Item
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sectionData.content_data.map((item, index) => {
                        const IconComponent = LucideIconMap[item.icon] || ShieldCheck;
                        const gradient = getColorGradient(item.color);

                        return (
                            <div
                                key={index}
                                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all group"
                            >
                                <div className={`h-1.5 bg-gradient-to-r ${gradient}`}></div>
                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} text-white shadow-sm`}>
                                            <IconComponent size={24} />
                                        </div>
                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => {
                                                    setEditingIndex(index);
                                                    setIsModalOpen(true);
                                                }}
                                                className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-primary-600 transition-colors"
                                                title="Edit"
                                            >
                                                <HeroIconsOutline.PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(index)}
                                                className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between mb-4">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${item.is_active !== false ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                            {item.is_active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    <div className="space-y-1">
                                        <p className="text-slate-800 text-sm font-medium leading-relaxed">
                                            {item.text}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )
                    })}

                    {sectionData.content_data.length === 0 && (
                        <div className="col-span-full py-12 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                                <ShieldCheck className="h-8 w-8 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-1">No compliance items added</h3>
                            <button
                                onClick={() => {
                                    setEditingIndex(null);
                                    setIsModalOpen(true);
                                }}
                                className="text-primary-600 font-medium hover:text-primary-700"
                            >
                                + Add First Item
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ComplianceModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                item={editingIndex !== null ? sectionData.content_data[editingIndex] : null}
                isEditing={editingIndex !== null}
            />
        </div>
    );
};

export default GovernanceEthicsPage;
